import React, { ReactElement, useState } from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { Button } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { useSidePanelContext } from 'modules/rootPage';
import { postRequest } from 'utils/helper';
import { ErrorPage } from '../routes/ErrorPage';
import { RepliconuserMenuContentPopUp } from './RepliconuserMenuContentPopUp';
import { RepliconMenuContent } from './RepliconMenuContent';
import { RepliconSubstituteUserBanner } from './RepliconSubstituteUserBanner';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeIn,
    duration: theme.transitions.duration.complex
  }),
  overflowX: 'hidden',
  '&:hover': {
    boxShadow:
      'inset -10px 0px 5px -10px rgb(0 0 0 / 0%), 0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)'
  }
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeIn,
    duration: theme.transitions.duration.complex
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)})`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)})`
  }
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  [theme.breakpoints.down('md')]: {
    display: 'none'
  },
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeIn,
    duration: theme.transitions.duration.complex,
    delay: 500
  }),
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme)
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme)
  }),
  '&:hover': {
    transition: theme.transitions.create(
      ['background-color', 'box-shadow', 'width'],
      {
        easing: theme.transitions.easing.easeIn,
        duration: theme.transitions.duration.complex
      }
    ),
    '& .MuiDrawer-paper': openedMixin(theme),
    '& .customLogoExpanded': {
      opacity: 1
    },
    '& .customLogoCollapsed': {
      opacity: 0
    }
  }
}));

const SwipeableDrawerMenu = (props: {
  menuPinned: boolean;
  swipeableDrawerOpen: boolean;
  navigationMetadata: any;
  isDocked: boolean;
  handleSwipeableDrawerClose: () => void;
  handleSwipeableDrawerOpen: () => void;
  handleDrawerOpen: () => void;
  handleSettingsPopUp: () => void;
  userMenuContentPopUp: boolean;
}): React.ReactElement => {
  return (
    <Box
      className={`pageheader ${props.menuPinned ? 'menuPinned' : ''}`}
      id="navigationPageHeader"
    >
      <SwipeableDrawer
        open={props.swipeableDrawerOpen}
        onClose={props.handleSwipeableDrawerClose}
        onOpen={props.handleSwipeableDrawerOpen}
      >
        <Box sx={{ display: 'flex' }}>
          <Box className="mainMenu">
            <Box
              className="pageheader
                  menuPinned"
              id="navigationPageHeader"
            >
              <RepliconMenuContent
                handleDrawerOpen={props.handleDrawerOpen}
                navigationMetadata={props.navigationMetadata}
                handleSettingsPopUp={props.handleSettingsPopUp}
                isDocked={!props.isDocked}
              ></RepliconMenuContent>
            </Box>
          </Box>
        </Box>
      </SwipeableDrawer>
    </Box>
  );
};

const DrawerMenu = (props: {
  menuPinned: boolean;
  navigationMetadata: any;
  isDocked: boolean;
  handleDrawerOpen: () => void;
  handleSettingsPopUp: () => void;
  userMenuContentPopUp: boolean;
}): React.ReactElement => {
  return (
    <Box className="pageheader" id="navigationPageHeader">
      <Drawer
        variant="permanent"
        open={props.menuPinned}
        id="drawer"
        className="drawer"
      >
        <RepliconMenuContent
          handleDrawerOpen={props.handleDrawerOpen}
          navigationMetadata={props.navigationMetadata}
          handleSettingsPopUp={props.handleSettingsPopUp}
          isDocked={!props.isDocked}
        ></RepliconMenuContent>
      </Drawer>
    </Box>
  );
};

export interface RepliconMenuHeaderProps {
  navigationMetadata: any;
}

export const RepliconMenuHeader = (props: {
  navigationMetadata: any;
}): ReactElement => {
  const { menuOpen, togglePinned, isDocked } = useSidePanelContext();
  const [showErrorPage, setShowErrorPage] = useState<boolean>(false);

  const initialize = React.useCallback(
    async (viewId: string, key: string, values: any) => {
      const body = {
        viewId: viewId,
        key: key,
        values: values
      };
      try {
        await postRequest('/integration-settings-api/ui-context', body, false);
      } catch (error: any) {
        if (error.response) {
          setShowErrorPage(true);
        }
      }
    },
    []
  );
  const handleDrawerOpen = (): void => {
    togglePinned();
    if (!menuOpen) {
      initialize('PinMenu', 'isPinned', [{ text: 'pinned' }]);
    } else {
      initialize('PinMenu', 'isPinned', [{ text: '' }]);
    }
  };
  const [userMenuContentPopUp, setUserMenuContentPopUp] = React.useState(false);
  const handleSettingsPopUp = (): void => {
    setUserMenuContentPopUp(!userMenuContentPopUp);
    setSwipeableDrawerOpen(false);
  };
  const handleSwipeableDrawerClose = (): void => {
    setSwipeableDrawerOpen(false);
  };
  const handleSwipeableDrawerOpen = (): void => {
    setSwipeableDrawerOpen(true);
  };
  document.addEventListener('mouseup', function (event: any) {
    const userMenuContentContainer = document.getElementById('userMenuContent');
    if (!userMenuContentContainer?.contains(event.target)) {
      setUserMenuContentPopUp(false);
    }
  });

  const [swipeableDrawerOpen, setSwipeableDrawerOpen] = React.useState(false);
  const handleActivateMenu = (): void => {
    setSwipeableDrawerOpen(true);
  };

  if (showErrorPage) {
    return <ErrorPage open={true} />;
  }
  return (
    <>
      <Box className={`${menuOpen ? 'menuPinned' : ''}`}>
        {props.navigationMetadata?.substituteUserMetadata && (
          <RepliconSubstituteUserBanner
            navigationMetadata={props.navigationMetadata}
            menuOpen={menuOpen}
            isDocked={!isDocked}
          />
        )}
        <Box
          id="ActivateMenuContainer"
          className="activateMenuContainer"
          sx={{
            position: 'absolute',
            top: '0px',
            left: '0px',
            marginTop:
              isDocked && props.navigationMetadata?.substituteUserMetadata
                ? '40px'
                : '0px'
          }}
        >
          <Button
            sx={{ color: '#000' }}
            className="activateMenuButton"
            type="button"
            onClick={handleActivateMenu}
            aria-label="Menu"
          >
            <svg
              className="svgIcon menuIcon"
              focusable="false"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"></path>
            </svg>
          </Button>
        </Box>
        <Box sx={{ display: 'flex' }}>
          <Box className="mainMenu">
            {isDocked ? (
              <SwipeableDrawerMenu
                menuPinned={menuOpen}
                swipeableDrawerOpen={swipeableDrawerOpen}
                navigationMetadata={props.navigationMetadata}
                isDocked={isDocked}
                handleSwipeableDrawerClose={handleSwipeableDrawerClose}
                handleSwipeableDrawerOpen={handleSwipeableDrawerOpen}
                handleDrawerOpen={handleDrawerOpen}
                handleSettingsPopUp={handleSettingsPopUp}
                userMenuContentPopUp={userMenuContentPopUp}
              ></SwipeableDrawerMenu>
            ) : (
              <DrawerMenu
                menuPinned={menuOpen}
                navigationMetadata={props.navigationMetadata}
                isDocked={isDocked}
                handleDrawerOpen={handleDrawerOpen}
                handleSettingsPopUp={handleSettingsPopUp}
                userMenuContentPopUp={userMenuContentPopUp}
              ></DrawerMenu>
            )}
          </Box>
          {userMenuContentPopUp && (
            <RepliconuserMenuContentPopUp
              navigationMetadata={props.navigationMetadata}
            ></RepliconuserMenuContentPopUp>
          )}
        </Box>
      </Box>
    </>
  );
};

export default RepliconMenuHeader;
